import React, { useState, useEffect, Fragment } from 'react';
import { Button, Card, Col, Row, FormCheck, Modal, ListGroup } from 'react-bootstrap';
import { Typography, Box, Tab, Tabs,
  Accordion, AccordionDetails, AccordionSummary, Autocomplete, TextField
 } from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { InputFormFiled } from '../../../components/Common/FormikField/InputFormFiled';
import { string_required, GetSchema } from '../../../js/Base/ValidationSchema';
import toastAlert from '../../../js/Base/ToastAlert';
import CloseIcon from '@mui/icons-material/Close';
import { ExpandMore } from '@mui/icons-material';
import { useSelector, useDispatch } from "react-redux";
import { fetchCompanyLicense } from '../../../store/slices/licenseSlice';
import { cloudNativeEnabledSelector, crowdstrikeEnabledSelector } from "../../../store/selectors/licenseSelectors";
import { getAuthorityTypes, createProjectRole, editProjectRole } from '../../../api/projectApi';

const ProjectRoleEditModal = ({ isModalOpen, toggleModal, submitEffect, isCreate, role }) => {
  const providers = ["AWS", "Azure", "GCP"];
  const [currTab1, setCurrTab1] = useState(providers[0]); // Cloud Native
  const [currTab2, setCurrTab2] = useState(providers[0]); // CrowdStrike
  const [projectAuthorityTypes, setProjectAuthorityTypes] = useState([]);
  const [selectedAuthTypeList, setSelectedAuthTypeList] = useState(role?.authority_code || []);
  const dispatch = useDispatch();
  const { email, isAdmin } = useSelector(state => state.user);
  useEffect(() => {
    setSelectedAuthTypeList(role?.authority_code || []);
  }, [role]);
  
  useEffect(() => {
    if (isModalOpen) {
      setCurrTab1(providers[0]);
      setCurrTab2(providers[0]);
    }
  }, [isModalOpen]);

  const fetchAuthorityTypes = async () => {
    try {
      const res = await getAuthorityTypes();
      const data = res.data;

      if (!data.success) {
        toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
        setTimeout(() => {
          document.location.assign(document.location.origin + "/");
        }, 3000);
        return;
      }
  
      setProjectAuthorityTypes(data.authorityType || []);
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    }
  };

  useEffect(() => {
    dispatch(fetchCompanyLicense());
    fetchAuthorityTypes();
  }, []);

  const cloudNativeEnabled = useSelector(cloudNativeEnabledSelector);
  const crowdstrikeEnabled = useSelector(crowdstrikeEnabledSelector);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const projectUuid = localStorage.getItem("project_uuid");
      const name = values.name;
      const desc = values.desc;
      const authorityCode = selectedAuthTypeList;
    
      if (isCreate) {
        await createProjectRoleByWebApi(projectUuid, name, desc, authorityCode,resetForm);
      } else {
        const projectRole = { ...role, name, desc, authority_code: authorityCode };
        await editProjectRoleByWebApi(projectRole, resetForm);
      }

    } catch (error) {
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
      handleClose(resetForm);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (resetForm) => {
    if (typeof resetForm === "function") resetForm();
    setSelectedAuthTypeList([]);
    toggleModal(null);
  };

  const createProjectRoleByWebApi = async (projectUuid, name, desc, authorityCode, resetForm) => {
    try {
      const payload = {
        project_uuid: projectUuid,
        name,
        desc,
        authority_code: authorityCode,
      };

      const res = await createProjectRole(payload);
      const data = res.data;

      if (data.success) {
        toastAlert
          .autoClose(true)
          .showSuccess(`成功建立角色: ${data.name || name}`);

        submitEffect();
        handleClose(resetForm);
      } else {
        toastAlert.autoClose(true).showError("建立失敗，請稍後再試");
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("建立失敗，請稍後再試");
    }
  };

  const editProjectRoleByWebApi = async (projectRole, resetForm) => {
    try {
      const payload = {
        role_uuid: projectRole.uuid,
        name: projectRole.name,
        desc: projectRole.desc,
        authority_code: projectRole.authority_code,
      };
  
      const res = await editProjectRole(payload);
      const data = res.data;

      if (data.success) {
        toastAlert
          .autoClose(true)
          .showSuccess(`編輯角色成功: ${projectRole.name}`);

        submitEffect();
        handleClose(resetForm);
      } else {
        toastAlert.autoClose(true).showError("編輯失敗，請稍後再試");
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("編輯失敗，請稍後再試");
    }
  };

  const initialValues = {
    name: role?.name || '',
    desc: role?.desc || '',
    auth: role?.authority_code || [],
  };

  const formSchema = GetSchema({
    name: string_required(),
  });

  const renderTabContent = (sourceSystem, currCloud) => {
    const filteredItems = projectAuthorityTypes.filter(
      (authType) => authType.source === sourceSystem && authType.cloud === currCloud);

    if (filteredItems.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          尚無資料
        </Typography>
      );
    }

    return (
      <Fragment>
        {filteredItems.map((authType) => (
          <Col md={6}
            className="auth-item"
            key={authType.code}>
            <FormCheck
              name="auth"
              className="primary-checkBox auth-check"
              inline
              value={authType.code}
              label={authType.standard}
              checked={selectedAuthTypeList.includes(authType.code)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedAuthTypeList((prev) => [...prev, authType.code]);
                } else {
                  setSelectedAuthTypeList((prev) =>
                    prev.filter((code) => code !== authType.code)
                  );
                }
              }}
            />
          </Col>
        ))}
      </Fragment>
    );
  };

  return (
    <>
      <Modal
        animation="true"
        style={{ zIndex: '8999' }}
        show={isModalOpen}
        onHide={toggleModal}
        className="ProjectManagement"
      >
        <Modal.Header>
          <Modal.Title className="modal-title">{isCreate ? '新增角色' : '編輯角色'}</Modal.Title>
        
          {/* 解決籃框問題，自訂MUI按鈕 */}
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginLeft: 'auto'
            }}
          >
            <CloseIcon fontSize="medium" />
          </button>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          validationSchema={formSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ handleSubmit, resetForm, setFieldValue, isSubmitting, errors, touched, values }) => (
            <Form>
              <Modal.Body className="modal-body" style={{ paddingTop: '0px'}}>
                <Row>
                  <Col md="12">
                    <InputFormFiled
                      title="角色名字"
                      className="input-field custom-select"
                      name="name"
                      type="text"
                      invalid={errors.name && touched.name}
                    />
                  </Col>
                  <Col md="12">
                    <InputFormFiled
                      title="說明"
                      className="input-field custom-select"
                      name="desc"
                      type="text"
                      invalid={errors.desc && touched.desc}
                    />
                  </Col>
                  <Col md={12}>
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#323232',
                        fontWeight: 'bold',
                        marginTop: '20px',
                        marginBottom: '6px',
                        fontFamily: 'Noto Sans, sans-serif',
                      }}
                    >
                      權限
                    </p>
                  </Col>

                  <div>
                    {isAdmin && role?.name === "eDC Admin" && (
                      <Col md={6} className="auth-item" key={role.id}>
                        <FormCheck
                          name="auth"
                          className="primary-checkBox auth-check"
                          inline
                          value={role.authority_code?.[0]}
                          label={role.name}
                          checked={selectedAuthTypeList.includes(role.authority_code?.[0])}
                          style={{
                            color: '#323232',
                            marginBottom: '6px',
                            fontFamily: 'Noto Sans, sans-serif',
                          }}
                          onChange={(e) => {
                            const code = role.authority_code?.[0];
                            if (!code) return;

                            if (e.target.checked) {
                              setSelectedAuthTypeList(prev => [...prev, code]);
                            } else {
                              setSelectedAuthTypeList(prev =>
                                prev.filter(c => c !== code)
                              );
                            }
                          }}
                        />
                      </Col>
                    )}

                    {cloudNativeEnabled && (
                      <Col md="12" style={{ padding: '0px', marginBottom: '24px' }}>
                        <Accordion
                          defaultExpanded
                          className="provider-accordion"
                        >
                          <AccordionSummary expandIcon={<ExpandMore style={{ color: '#E2E2E2' }} />} className="provider-title">
                            Cloud Native
                          </AccordionSummary>
                          <AccordionDetails style={{ border: '1px solid #E2E2E2', borderLeft: 'None', borderRight: 'None', padding: '0px' }}>
                          {/* <Box className="provider-section"> */}
                          {/* <Typography className="provider-title">Cloud Native</Typography> */}

                            <Tabs
                              value={currTab1}
                              onChange={(e, tab) => setCurrTab1(tab)}
                              className="provider-tabs"
                            >
                              {providers.map((provider) => (
                                <Tab key={provider} value={provider} label={provider} />
                              ))}
                            </Tabs>

                            <Box className="provider-content">
                              {renderTabContent("Cloud Native", currTab1)}
                            </Box>
                          {/* </Box> */}
                          </AccordionDetails>
                        </Accordion>
                      </Col>
                    )}

                    {crowdstrikeEnabled && (
                      <Col md="12" style={{ padding: '0px', marginBottom: '24px' }}>
                        <Accordion
                          defaultExpanded
                          className="provider-accordion"
                        >
                          <AccordionSummary expandIcon={<ExpandMore style={{ color: '#E2E2E2' }} />} className="provider-title">
                            CrowdStrike
                          </AccordionSummary>
                          <AccordionDetails style={{ border: '1px solid #E2E2E2', borderLeft: 'None', borderRight: 'None', padding: '0px' }}>
                            {/* <Box className="provider-section"> */}
                            {/* <Typography className="provider-title">CrowdStrike</Typography> */}

                            <Tabs
                              value={currTab2}
                              onChange={(e, tab) => setCurrTab2(tab)}
                              className="provider-tabs"
                            >
                              {providers.map((provider) => (
                                <Tab key={provider} value={provider} label={provider} />
                              ))}
                            </Tabs>

                            <Box className="provider-content">
                              {renderTabContent("CrowdStrike", currTab2)}
                            </Box>
                          {/* </Box> */}
                          </AccordionDetails>
                        </Accordion>
                      </Col>
                    )}

                    {!cloudNativeEnabled && !crowdstrikeEnabled && (
                      <span style={{ paddingLeft: 10, color: '#999' }}>
                        目前無任何授權
                      </span>
                    )}
                  </div>

                </Row>
              </Modal.Body>
              <Modal.Footer className="modal-footer">
                <Button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => handleClose(resetForm)}
                  disabled={isSubmitting}
                >
                  取消
                </Button>
                <Button type="submit" className="btn btn-custom" disabled={isSubmitting} >
                  確認
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default ProjectRoleEditModal;
